/**
 * My staff profile + display-zone sync (2026-08-07, Pat).
 *
 * A staff member carries a default time zone on their roster record so their
 * appointments can be read in THEIR wall clock. Two consumers:
 *
 *  • useStaffTimezoneSync() — mounted once at the app root. Applies the
 *    signed-in member's profile zone as the app display zone. Precedence
 *    (declared in lib/datetime): explicit user preference > staff profile >
 *    workspace/tenant > browser. Venue-anchored records (an event's own
 *    timezone, a branch's wall-clock shift times) are never re-zoned by it.
 *  • useMyStaffProfile() — the Account ▸ Details self-service row.
 *
 * Both read `getMyStaffProfile` (authenticated-session, NOT staffing.read):
 * a banker must be able to set their own zone without holding roster admin,
 * and the app root must not pull the whole roster at boot to find one field.
 * Not on the roster ⇒ `data: null` ⇒ the workspace default stands.
 */
import { useCallback, useEffect, useState } from "react";
import { getMyStaffProfile, setMyStaffTimezone } from "@v0y4ge/api-client";
import type { CanonicalStaffMember } from "@v0y4ge/api-contracts";
import { applyStaffProfileTimezone } from "../../lib/datetime";
import { isSignedIn } from "../../shell/access";

export type MyStaffProfile = CanonicalStaffMember & { timezone?: string | null };

/* One shared read per session — the root hook and the account row must not
   fire two requests, and the account row's save has to fan out to the sync. */
let _profile: MyStaffProfile | null = null;
let _loaded = false;
let _inflight: Promise<void> | null = null;
const _subs = new Set<() => void>();
const _notify = () => _subs.forEach((fn) => fn());

function load(force = false): Promise<void> {
  if (_inflight) return _inflight;
  if (_loaded && !force) return Promise.resolve();
  _inflight = Promise.resolve()
    .then(() => (typeof getMyStaffProfile === "function" ? getMyStaffProfile() : Promise.resolve({ data: null })))
    .then((r) => { _profile = ((r as { data?: MyStaffProfile | null })?.data) ?? null; })
    .catch((e) => { console.warn("getMyStaffProfile failed:", e); _profile = null; })
    .then(() => { _loaded = true; _inflight = null; _notify(); });
  return _inflight;
}

function useProfileStore() {
  const [, force] = useState(0);
  useEffect(() => {
    const fn = () => force((n) => n + 1);
    _subs.add(fn);
    if (!_loaded && isSignedIn()) void load();
    return () => { _subs.delete(fn); };
  }, []);
  return { profile: _profile, loaded: _loaded };
}

/** Mount once at the app root. */
export function useStaffTimezoneSync(): void {
  const { profile, loaded } = useProfileStore();
  const tz = (profile?.timezone as string | null | undefined) ?? null;
  useEffect(() => {
    if (!loaded) return; // never clear a mirrored zone while the read is in flight
    applyStaffProfileTimezone(tz);
  }, [tz, loaded]);
}

/** Account ▸ Details self-service row. */
export function useMyStaffProfile() {
  const { profile, loaded } = useProfileStore();
  const save = useCallback(async (tz: string | null) => {
    const r = await setMyStaffTimezone(tz);
    _profile = ((r as { data?: MyStaffProfile | null })?.data) ?? _profile;
    _loaded = true;
    _notify();
    // Apply immediately — the whole point is that the change is visible now.
    applyStaffProfileTimezone((_profile?.timezone as string | null | undefined) ?? null);
    return _profile;
  }, []);
  return { profile, loading: !loaded, setTimezone: save, reload: () => load(true) };
}
